/**
 * 系统健康透视 — the unions and the joins.
 *
 * This is the only place that decides WHICH nodes exist. `model.ts` assembles
 * and orders them; `model-nodes.ts` does the arithmetic inside one. Keeping
 * the union logic here is deliberate: every one of the joins below is between
 * two wire shapes that were designed independently, and the key choices are
 * the part of this page most likely to be got wrong by someone reading only
 * one of the three sources.
 *
 * The collectors take the individual slices rather than the whole
 * `PerspectiveInput` so this module has no dependency on `model.ts` — the
 * arrow points one way, and `PerspectiveInput` stays declared next to the
 * function that consumes it.
 *
 * A node is created from ANY of its sources, never only from the intersection:
 * a SKU with routing targets and no traffic must appear (its silence is the
 * finding), and a ledger bucket with money and no config must appear (the
 * totals only reconcile when it does).
 *
 * ── The three supply grains ──
 *
 * `TrafficProvenance` in `types.ts` states them: upstream (11 in production),
 * upstream channel (21), upstream model (73). Only the first two are measured.
 * A routing target terminates at the third — `upstream_channel_slug` +
 * `upstream_model_name` — so that is where a link ends, and it is also the one
 * grain no source can state a rate for.
 *
 * The grains therefore do NOT line up with the sources, and each mismatch is
 * resolved in exactly one direction, downward, never by inventing a split:
 *
 *   traffic  channel  → model    borrowed, labelled `channel_borrowed`
 *   money    upstream → channel  borrowed, see `owningUpstreamMoney` below
 */

import type {
  BusinessSkuEconomicsRow,
  OpsTrafficBreakdownRow,
  RevenueByUpstreamRow,
  RoutingConfigSkuSummaryTarget,
} from '@nadoutong/contracts';
import { supplyFailureFigures } from '../../components/trafficHealth.ts';
import {
  EMPTY_TRAFFIC,
  UNSETTLED_MONEY,
  aggregateTraffic,
  moneyFromSkuEconomics,
  moneyFromUpstreamRevenue,
  refusedBeforeUpstreamOf,
  reportsRefusals,
  trafficFromRow,
} from './model-nodes.ts';
import type { MeasuredLeg, NodeMoney, ObservedTraffic } from './types.ts';

/**
 * Id segment for every bucket whose real key is absent. They need a stable
 * node id, and a literal empty segment (`'sku:'`) reads as a bug at every call
 * site that logs one. A marketplace SKU or channel literally named
 * `__unattributed__` would collide; none exists and the id space is
 * operator-assigned.
 */
export const UNATTRIBUTED = '__unattributed__';

/**
 * The key one `SupplyChannelNode` is stored and looked up under.
 *
 * `(upstream, channel id)`, NOT the slug and not the channel id alone.
 *
 * Slugs are plainly not unique across upstreams: `seedance` exists under both
 * `jojokey` and `ucloud` in production, and a slug-keyed map merges them into
 * one node that books both suppliers' traffic to whichever upstream id
 * happened to arrive first, routes the other's ledger money to a synthetic
 * orphan, and reports one channel where there are two.
 *
 * The channel ID alone looks sound and is not. `upstream_channels`'s primary
 * key is `(upstream_id, channel_id)` and `channel_id` is operator-supplied
 * free text, so the global uniqueness an id-keyed map assumes is a property of
 * today's data (30 channels, 30 distinct ids, 2026-09-01), not a constraint —
 * one operator creating a second `seedance` under another upstream would
 * silently re-create the merge. The pair is what the schema guarantees, and
 * every source carries it: `supply.channels` and `supply.skuChannels` both
 * project `upstreamId` + `upstreamChannelId`, routing targets carry
 * `upstream_id` + `upstream_channel_id`.
 *
 * The fallbacks, in order, none of which invents an identity (deliberately
 * not preceded by a count — a count is the part of a list that goes stale):
 *
 *   - an id with no upstream (a gateway too old to project one on this group):
 *     the bare id, which is what the previous key was;
 *   - no id but a slug and an upstream: the pair, which still separates two
 *     same-named channels of different suppliers;
 *   - no id and no upstream: the bare slug, which is all there is;
 *   - a row that names an upstream but no channel keys off the upstream, so an
 *     upstream's ledger money still lands somewhere rather than vanishing at a
 *     grain it cannot be stated at. The node's `channelSlug` stays null;
 *   - a row that names neither is the fully unattributed bucket.
 */
export function channelKey(
  channelId: string | null,
  slug: string | null,
  upstreamId: string | null,
): string {
  const upstream = upstreamId !== null && upstreamId.length > 0 ? upstreamId : null;
  if (channelId !== null && channelId.length > 0) {
    return upstream === null ? channelId : `${upstream}/${channelId}`;
  }
  if (slug !== null && slug.length > 0) {
    return upstream === null ? slug : `${upstream}/${slug}`;
  }
  if (upstream !== null) return `${UNATTRIBUTED}:${upstream}`;
  return UNATTRIBUTED;
}

/**
 * The key one `UpstreamModelNode` is stored under — `${channel}/${model}`, the
 * form `types.ts` documents.
 *
 * The first segment is the CHANNEL KEY — `channelKey`'s `(upstream, channel)`
 * pair — never the raw slug and never the bare channel id. Both of those are
 * ambiguous across upstreams, and a model node keyed on either would claim to
 * be two suppliers' endpoints at once.
 */
export function upstreamModelKey(channel: string, modelName: string | null): string {
  return `${channel}/${modelName ?? UNATTRIBUTED}`;
}

/** Everything one SKU node needs, gathered from up to three sources. */
export interface SkuAccumulator {
  readonly skuId: string | null;
  name: string | null;
  traffic: ObservedTraffic;
  /** Whether any traffic row was folded in — a genuine 0-request row is not "absent". */
  hasTrafficRow: boolean;
  /**
   * Whether EVERY folded traffic row published a refusal count.
   *
   * The downstream verdict subtracts refusals from both sides of its ratio, so
   * one row that cannot report them makes the total unusable — not
   * approximately right. False therefore means the SKU grades `unknown`
   * rather than being graded on a silently-zeroed subtraction. See
   * `gradeSkuTraffic`.
   */
  refusalsReported: boolean;
  money: NodeMoney;
  coverage: number | null;
  coverageStatus: 'covered' | 'single_point' | 'unrouted' | null;
}

/** Everything one channel node needs. The finest MEASURED supply grain. */
export interface ChannelAccumulator {
  readonly key: string;
  /** `upstream_channel_slug`; null when only an upstream was named. */
  channelSlug: string | null;
  upstreamId: string | null;
  /**
   * `upstreamName` off the traffic row.
   *
   * The wire type carries it, but neither the gateway's supply projection nor
   * ops-api's `decorateRow` fills it in today, so in practice this stays null
   * and the slug is the display name. It is still read rather than dropped:
   * the field is contractual, and the day it is populated is the day it should
   * win, not the day someone notices it never did.
   */
  upstreamName: string | null;
  traffic: ObservedTraffic;
  hasTrafficRow: boolean;
  /**
   * The OWNING UPSTREAM's money — not this channel's own.
   *
   * `revenue/by-upstream` is keyed by `upstream_id`, which is coarser than a
   * channel: an upstream with three channels has one ledger figure and no
   * per-channel breakdown exists anywhere. So the upstream's figure is
   * borrowed down onto each of its channels, exactly the way a channel's
   * traffic is borrowed down onto each of its models.
   *
   * That makes it a weaker claim than the field name `money` would suggest,
   * which is why this one is not called that: the same figure appears on every
   * sibling channel, so it must be read as "this channel's upstream earned
   * this" and must NOT be summed across channels. Splitting it by request
   * share instead would be an invented number, and attributing it to one
   * sibling would be a wrong one.
   */
  owningUpstreamMoney: NodeMoney;
}

/**
 * Everything one (channel × upstream model) node needs.
 *
 * Sourced from routing targets alone: neither money endpoint nor the traffic
 * overview has a group at this grain. Traffic is borrowed from the channel by
 * `model.ts`; there is no money here at all, and `types.ts` declines to give
 * `UpstreamModelNode` a money field for that reason.
 */
export interface UpstreamModelAccumulator {
  readonly key: string;
  readonly channelKey: string;
  channelSlug: string | null;
  upstreamId: string | null;
  upstreamModelName: string | null;
  capabilityStatus: string | null;
  capabilityId: string | null;
}

/**
 * Keyed by `string | null` so the unattributed SKU bucket needs no sentinel
 * key — and so no real id can ever collide with the bucket by accident.
 */
export type SkuAccumulators = ReadonlyMap<string | null, SkuAccumulator>;
export type ChannelAccumulators = ReadonlyMap<string, ChannelAccumulator>;
export type UpstreamModelAccumulators = ReadonlyMap<string, UpstreamModelAccumulator>;

function ensureSku(
  accumulators: Map<string | null, SkuAccumulator>,
  skuId: string | null,
): SkuAccumulator {
  const existing = accumulators.get(skuId);
  if (existing !== undefined) return existing;
  const created: SkuAccumulator = {
    skuId,
    name: null,
    traffic: EMPTY_TRAFFIC,
    hasTrafficRow: false,
    // Vacuously true until a row proves otherwise, so a SKU with no traffic
    // row at all still reaches the `idle` verdict rather than `unknown`.
    refusalsReported: true,
    money: UNSETTLED_MONEY,
    coverage: null,
    coverageStatus: null,
  };
  accumulators.set(skuId, created);
  return created;
}

function ensureChannel(
  accumulators: Map<string, ChannelAccumulator>,
  channelId: string | null,
  slug: string | null,
  upstreamId: string | null,
): ChannelAccumulator {
  const key = channelKey(channelId, slug, upstreamId);
  const existing = accumulators.get(key);
  if (existing !== undefined) {
    // First non-null wins for both identity fields: a later row that omits one
    // must not blank an identity an earlier row already established.
    existing.channelSlug = existing.channelSlug ?? slug;
    existing.upstreamId = existing.upstreamId ?? upstreamId;
    return existing;
  }
  const created: ChannelAccumulator = {
    key,
    channelSlug: slug,
    upstreamId,
    upstreamName: null,
    traffic: EMPTY_TRAFFIC,
    hasTrafficRow: false,
    owningUpstreamMoney: UNSETTLED_MONEY,
  };
  accumulators.set(key, created);
  return created;
}

/**
 * Fold one traffic row in.
 *
 * The gateway groups these server-side, so the first row for a node is taken
 * verbatim — passing it through the aggregator would recompute its rates from
 * the counts and could differ from the server's own division in the last float
 * bits. A repeated group (which a well-formed response never contains) is
 * summed rather than silently overwriting the earlier row.
 */
function mergeTraffic(
  current: ObservedTraffic,
  hasTrafficRow: boolean,
  row: OpsTrafficBreakdownRow,
): ObservedTraffic {
  if (!hasTrafficRow) return trafficFromRow(row);
  return aggregateTraffic([current, trafficFromRow(row)]);
}

/**
 * The SKU union.
 *
 * All three sources carry the marketplace SKU id (`skuId` on the two camelCase
 * shapes, `sku_id` on the routing row), so that — and not the display name —
 * is the join key. `skuId: null` is the economics legacy bucket and keys to
 * itself; a traffic row with a null id lands in the same bucket, which is the
 * honest reading of "this usage was never attributed to a SKU".
 */
export function collectSkus(
  trafficSkus: readonly OpsTrafficBreakdownRow[],
  economicsSkus: readonly BusinessSkuEconomicsRow[],
  targets: readonly RoutingConfigSkuSummaryTarget[],
): SkuAccumulators {
  const accumulators = new Map<string | null, SkuAccumulator>();

  // 1. Measured traffic. `products.skus` is absent on pre-#914 payloads, which
  //    is a real deployment state rather than an error — the page then shows
  //    the configured SKUs with no observed traffic, not nothing at all.
  for (const row of trafficSkus) {
    const accumulator = ensureSku(accumulators, row.skuId ?? null);
    accumulator.traffic = mergeTraffic(accumulator.traffic, accumulator.hasTrafficRow, row);
    accumulator.hasTrafficRow = true;
    // AND, not OR: the subtraction needs every contributing row to have
    // reported, and one silent row poisons the total.
    accumulator.refusalsReported = accumulator.refusalsReported && reportsRefusals(row);
    accumulator.name = accumulator.name ?? row.skuName ?? null;
  }

  // 2. Money. The caller passes an empty list when the economics section came
  //    back null (#1002 convention: a null section always has a matching
  //    warning, which the caller has already recorded as a `SliceFailure`).
  for (const row of economicsSkus) {
    const accumulator = ensureSku(accumulators, row.skuId);
    accumulator.money = moneyFromSkuEconomics(row);
    accumulator.coverage = row.coverage;
    accumulator.coverageStatus = row.coverageStatus;
    accumulator.name = accumulator.name ?? row.skuName ?? null;
  }

  // 3. Configured-but-silent SKUs. A SKU that owns a routing target and appears
  //    in neither source above still gets a node: that silence is the finding
  //    ("we shelved something and nobody noticed"), and it grades `idle`
  //    because there is nothing to judge — not `ok`.
  for (const target of targets) {
    const accumulator = ensureSku(accumulators, target.sku_id);
    accumulator.name = accumulator.name ?? target.sku_name;
  }

  return accumulators;
}

/** Channels this upstream owns, in insertion order. Never a fuzzy match. */
function channelsOwnedBy(
  accumulators: ReadonlyMap<string, ChannelAccumulator>,
  upstreamId: string,
): readonly ChannelAccumulator[] {
  return [...accumulators.values()].filter(
    (accumulator) => accumulator.upstreamId === upstreamId,
  );
}

/**
 * The channel union — the finest MEASURED supply grain.
 *
 * Traffic comes from `supply.channels`, NOT `supply.upstreams`: an upstream
 * aggregates several channels, so an upstream-grain error rate cannot say
 * which of its channels is the broken one. `supply.channels` rows carry the
 * slug, the channel id and the owning upstream id (gateway
 * `internal-traffic.ts`), so the key is the `channelKey` pair — never the
 * slug — and the upstream comes along for the money join too.
 *
 * The display NAME cannot be joined the way the ids are: upstream names are
 * gateway-owned, and platform-api fills the revenue row's `label` with
 * `upstream_id ?? '(unknown)'`. So `label` is never read here — it is an id
 * wearing a name's field, and rendering it would put a raw id (or the literal
 * string `(unknown)`) on the page as a supply node's name.
 */
export function collectChannels(
  trafficChannels: readonly OpsTrafficBreakdownRow[],
  revenueUpstreams: readonly RevenueByUpstreamRow[],
  targets: readonly RoutingConfigSkuSummaryTarget[],
): ChannelAccumulators {
  const accumulators = new Map<string, ChannelAccumulator>();

  for (const row of trafficChannels) {
    const accumulator = ensureChannel(
      accumulators,
      row.upstreamChannelId ?? null,
      row.upstreamChannelSlug ?? null,
      row.upstreamId ?? null,
    );
    accumulator.traffic = mergeTraffic(accumulator.traffic, accumulator.hasTrafficRow, row);
    accumulator.hasTrafficRow = true;
    accumulator.upstreamName = accumulator.upstreamName ?? row.upstreamName ?? null;
  }

  // Configured channels with no traffic row. Their silence is a finding for the
  // same reason a silent SKU's is, so they get a node and grade `idle`.
  for (const target of targets) {
    ensureChannel(
      accumulators,
      target.upstream_channel_id ?? null,
      target.upstream_channel_slug ?? null,
      target.upstream_id ?? null,
    );
  }

  // Money, borrowed DOWN from the upstream grain — see `owningUpstreamMoney`.
  //
  // The ledger's `upstream_id: null` bucket goes to the fully unattributed
  // channel and nowhere else. It is never spread over named channels: money the
  // ledger could not attribute to an upstream must not arrive on the page as
  // some named channel's earnings.
  //
  // A named upstream that owns no channel here — its channel row fell outside
  // the traffic response's top-100, or rating settled a window the traffic
  // slice no longer covers — gets a node keyed off the upstream with a null
  // `channelSlug`, so its money is visible rather than silently dropped.
  for (const row of revenueUpstreams) {
    const money = moneyFromUpstreamRevenue(row);
    if (row.upstream_id === null) {
      ensureChannel(accumulators, null, null, null).owningUpstreamMoney = money;
      continue;
    }
    const owned = channelsOwnedBy(accumulators, row.upstream_id);
    if (owned.length === 0) {
      ensureChannel(accumulators, null, null, row.upstream_id).owningUpstreamMoney = money;
      continue;
    }
    for (const accumulator of owned) accumulator.owningUpstreamMoney = money;
  }

  return accumulators;
}

/**
 * The (channel × upstream model) union — where routing targets actually land.
 *
 * Routing targets are the only source: there is no traffic group and no ledger
 * grouping at this grain, which is the whole reason `UpstreamModelNode` borrows
 * its traffic and carries no money.
 *
 * The capability pin is taken first-non-null. Two targets naming the same
 * channel and model with different capability ids is a config the pool permits
 * and the page cannot render as two nodes — the node is the physical endpoint,
 * and the per-target pin stays on the link, where it belongs.
 */
export function collectUpstreamModels(
  targets: readonly RoutingConfigSkuSummaryTarget[],
): UpstreamModelAccumulators {
  const accumulators = new Map<string, UpstreamModelAccumulator>();

  for (const target of targets) {
    const slug = target.upstream_channel_slug ?? null;
    const upstreamId = target.upstream_id ?? null;
    // By channel ID: two suppliers own a channel called `seedance`, and a
    // slug-keyed model node would claim to be both of them at once.
    const channel = channelKey(target.upstream_channel_id ?? null, slug, upstreamId);
    const key = upstreamModelKey(channel, target.upstream_model_name ?? null);
    const existing = accumulators.get(key);
    if (existing === undefined) {
      accumulators.set(key, {
        key,
        channelKey: channel,
        channelSlug: slug,
        upstreamId,
        upstreamModelName: target.upstream_model_name ?? null,
        capabilityStatus: target.capability_status ?? null,
        capabilityId: target.capability_id ?? null,
      });
      continue;
    }
    existing.channelSlug = existing.channelSlug ?? slug;
    existing.upstreamId = existing.upstreamId ?? upstreamId;
    existing.capabilityStatus = existing.capabilityStatus ?? target.capability_status ?? null;
    existing.capabilityId = existing.capabilityId ?? target.capability_id ?? null;
  }

  return accumulators;
}

/**
 * The measured legs, keyed by (SKU × channel).
 *
 * The same key shape a `SupplyLink` can build from its own fields, so
 * `model.ts` joins rungs to legs without a second pass over the wire rows.
 *
 * The second segment is a `channelKey` — `(upstream, channel id)` — not a
 * slug and not a bare id, for the reasons `channelKey` states. A row that
 * names neither a channel nor a slug cannot be attached to any rung and is
 * skipped rather than guessed onto one: a leg placed on the wrong rung would
 * be worse than a missing leg, which at least renders as unknown.
 *
 * Duplicate pairs are summed rather than overwritten. The wire emits one row
 * per observed pair today, but a future grouping change that split a pair
 * across rows must not silently drop volume.
 */
export function legKey(skuId: string | null, channel: string | null): string {
  return `${skuId ?? UNATTRIBUTED}::${channel ?? UNATTRIBUTED}`;
}

/**
 * The leg index: one sound lookup and two fallbacks that refuse to guess.
 *
 * `byChannel` is keyed by `channelKey` and is what a rung uses whenever both
 * sides name an upstream and a channel id, which is every production row.
 *
 * `byChannelId` is keyed by the BARE `upstreamChannelId`, and exists for one
 * window only: a console deployed ahead of the gateway that first projected
 * `upstreamId` on this group. Without it every rung on the page would miss
 * its leg and render 无流量 — a fabricated "broken rung", which is exactly the
 * finding an operator is meant to act on.
 *
 * `bySlug` exists for a rung that has no channel id at all.
 *
 * Both fallbacks DROP any key that two different legs of the same SKU share.
 * Returning nothing there is deliberate: the alternative is handing both rungs
 * the same leg, which is how one supplier's volume gets rendered under
 * another's name and how a SKU's rungs end up summing to 200% of its own
 * traffic.
 */
export interface LegIndex {
  readonly byChannel: ReadonlyMap<string, MeasuredLeg>;
  readonly byChannelId: ReadonlyMap<string, MeasuredLeg>;
  readonly bySlug: ReadonlyMap<string, MeasuredLeg>;
}

/** Record that `fallback` could mean `primary`. Two claimants disqualify it. */
function claim(owners: Map<string, Set<string>>, fallback: string, primary: string): void {
  const existing = owners.get(fallback);
  if (existing === undefined) owners.set(fallback, new Set([primary]));
  else existing.add(primary);
}

/** A fallback index over the unambiguous claims only. */
function resolveFallback(
  owners: ReadonlyMap<string, Set<string>>,
  legs: ReadonlyMap<string, MeasuredLeg>,
): ReadonlyMap<string, MeasuredLeg> {
  const resolved = new Map<string, MeasuredLeg>();
  for (const [fallback, primaries] of owners) {
    if (primaries.size !== 1) continue;
    const [only] = [...primaries];
    const leg = only === undefined ? undefined : legs.get(only);
    if (leg !== undefined) resolved.set(fallback, leg);
  }
  return resolved;
}

/**
 * The SKU's tenant-request total this row's share was taken against: the
 * gateway's `skuRequestCount` where published, else the wire share unwound
 * (`requestCount / requestShare`). 0 when neither can say — a row with no
 * requests and no total contributes no denominator, and the leg's share then
 * reads 0 rather than a division by nothing.
 */
function skuRequestTotalOf(row: OpsTrafficBreakdownRow): number {
  if (row.skuRequestCount !== undefined) return row.skuRequestCount;
  return row.requestShare > 0 ? row.requestCount / row.requestShare : 0;
}

/** `a + b`, null if either side is unreported — the AND rule of `collectSkus`. */
function addNullable(a: number | null, b: number | null): number | null {
  return a === null || b === null ? null : a + b;
}

export function collectSkuChannelLegs(rows: readonly OpsTrafficBreakdownRow[]): LegIndex {
  const summed = new Map<string, Omit<MeasuredLeg, 'shareOfSku'>>();
  // The SKU each leg belongs to, and that SKU's tenant-request total — the
  // denominator of `shareOfSku` (see `MeasuredLeg` in types.ts): every tenant
  // request is exactly one first attempt, so "share of requests this rung was
  // dialled for" is comparable to a declared first-attempt share, where
  // "share of all dials" is not. The total is the GATEWAY's (`skuRequestCount`,
  // or the wire's own share unwound where an older gateway omits it), never a
  // sum over the rows received: rows with no channel key are skipped above
  // and the group is capped, and both would shrink a client-side sum.
  const skuOfLeg = new Map<string, string | null>();
  const skuRequests = new Map<string | null, number>();
  // fallback key -> the primary keys seen under it, per SKU. More than one
  // means the fallback cannot identify a leg and must refuse to answer.
  const slugOwners = new Map<string, Set<string>>();
  const bareIdOwners = new Map<string, Set<string>>();
  for (const row of rows) {
    const slug = row.upstreamChannelSlug ?? null;
    const channelId = row.upstreamChannelId ?? null;
    if (slug === null && channelId === null) continue;
    const skuId = row.skuId ?? null;
    const key = legKey(skuId, channelKey(channelId, slug, row.upstreamId ?? null));
    if (slug !== null) claim(slugOwners, legKey(skuId, slug), key);
    if (channelId !== null) claim(bareIdOwners, legKey(skuId, channelId), key);
    const existing = summed.get(key);
    const requests = (existing?.requests ?? 0) + row.requestCount;
    const errors = (existing?.errors ?? 0) + row.errorCount;
    const serverErrors = (existing?.serverErrors ?? 0) + row.serverErrorCount;
    // AND across contributing rows, exactly as `collectSkus` does for a SKU:
    // one silent row poisons the subtraction, so the whole leg reports
    // unknown rather than a total computed from a substituted zero. The
    // three #2240 counters follow the same rule.
    const refusedBeforeUpstream = addNullable(
      existing === undefined ? 0 : existing.refusedBeforeUpstream,
      refusedBeforeUpstreamOf(row),
    );
    const upstreamAttemptFailures = addNullable(
      existing === undefined ? 0 : existing.upstreamAttemptFailures,
      row.upstreamAttemptFailureCount ?? null,
    );
    const upstreamFinalFailures = addNullable(
      existing === undefined ? 0 : existing.upstreamFinalFailures,
      row.upstreamFinalFailureCount ?? null,
    );
    const supply = supplyFailureFigures({ requests, refusedBeforeUpstream, upstreamAttemptFailures, upstreamFinalFailures });
    // Dialled = tenant-final rows + the attempt failures the ladder moved
    // past. Where the gateway cannot report the latter this is `requests`,
    // and the share below reduces to the wire's own.
    const dialled = requests + (upstreamAttemptFailures ?? 0);
    summed.set(key, {
      requests,
      dialled,
      errors,
      errorRate: requests === 0 ? 0 : errors / requests,
      serverErrors,
      serverErrorRate: requests === 0 ? 0 : serverErrors / requests,
      upstreamAttemptFailures,
      upstreamAttemptRateLimited: addNullable(
        existing === undefined ? 0 : existing.upstreamAttemptRateLimited,
        row.upstreamAttemptRateLimitedCount ?? null,
      ),
      upstreamRefused: addNullable(
        existing === undefined ? 0 : existing.upstreamRefused,
        row.upstreamRefusedCount ?? null,
      ),
      upstreamFinalFailures,
      supplyFailures: supply === null ? null : supply.failures,
      supplyFailureRate: supply === null ? null : supply.rate,
      ...legFailureFigures(requests, errors, refusedBeforeUpstream),
    });
    skuOfLeg.set(key, skuId);
    skuRequests.set(skuId, Math.max(skuRequests.get(skuId) ?? 0, skuRequestTotalOf(row)));
  }

  // The share is this leg's DIALS over the SKU's tenant requests, computed
  // here rather than read from the wire's `requestShare` (tenant-final rows
  // over the same denominator). A primary leg whose 429s were all rescued
  // would otherwise measure below its declared share and be flagged as
  // divergence for a ladder doing exactly what it was told. Where the
  // gateway reports no attempt failures this is exactly the wire's figure.
  const legs = new Map<string, MeasuredLeg>();
  for (const [key, leg] of summed) {
    const total = skuRequests.get(skuOfLeg.get(key) ?? null) ?? 0;
    legs.set(key, { ...leg, shareOfSku: total === 0 ? 0 : Math.min(1, leg.dialled / total) });
  }

  return {
    byChannel: legs,
    byChannelId: resolveFallback(bareIdOwners, legs),
    bySlug: resolveFallback(slugOwners, legs),
  };
}

/**
 * The net figures for one leg. Refusals are a strict subset of errors, so
 * removing them from BOTH sides is sound — but this is NOT the same
 * subtraction the SKU grain performs, and reading it as such is bug 7.
 *
 * A SKU deducts the WHITELIST (`downstreamFigures` in `model-nodes.ts`),
 * which leaves `MODEL_NOT_AVAILABLE` counted against us. A leg has no code
 * split on the wire at all, so it can only deduct the STRUCTURAL scalar —
 * every failure that never dialled an upstream, our own routing gaps
 * included. That over-deducts, always in the direction that flatters us.
 *
 * Hence the figures this returns are a FLOOR, not a rate, and
 * `supplyVerdict.ts` consumes them as the lower end of a bound: it returns
 * `unknown` where a clean floor sits under a dirty ceiling, and `LadderRow`
 * prints the `≥` markers that say so. Anything that treats this return value
 * as "the leg's failure rate" reintroduces the bug.
 *
 * Null propagates. A leg whose refusals were never reported has no verdict,
 * and every consumer renders that as unknown rather than as a clean rate.
 */
function legFailureFigures(
  requests: number,
  errors: number,
  refusedBeforeUpstream: number | null,
): {
  readonly refusedBeforeUpstream: number | null;
  readonly attributableRequests: number | null;
  readonly attributableErrors: number | null;
  readonly failureRate: number | null;
} {
  if (refusedBeforeUpstream === null) {
    return {
      refusedBeforeUpstream: null,
      attributableRequests: null,
      attributableErrors: null,
      failureRate: null,
    };
  }
  // Clamped for the same reason as the SKU-grain version: counts published by
  // two different aggregations must not produce a negative population.
  const attributableRequests = Math.max(0, requests - refusedBeforeUpstream);
  const attributableErrors = Math.max(0, errors - refusedBeforeUpstream);
  return {
    refusedBeforeUpstream,
    attributableRequests,
    attributableErrors,
    failureRate: attributableRequests === 0 ? 0 : attributableErrors / attributableRequests,
  };
}
